import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import {
  Banknote, Loader, AlertCircle, X, CheckCircle, ChevronLeft, ChevronRight,
  Printer, Upload, FileSpreadsheet, Plus, Calendar, Users, RefreshCw,
  TrendingUp, Lock, Shield, Send, Download, Star, ChevronDown, ChevronUp,
  CreditCard,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v) =>
  `PKR ${Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm disabled:bg-gray-50';
const labelClass =
  'block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

// Period: 11th of previous month → 10th of current month
const getPeriod = (year, month) => {
  const pad = (n) => String(n).padStart(2, '0');
  const prevYear = month === 1 ? year - 1 : year;
  const prevMonth = month === 1 ? 12 : month - 1;
  return {
    start: `${prevYear}-${pad(prevMonth)}-11`,
    end: `${year}-${pad(month)}-10`,
  };
};

const printSalarySlip = (payment) => {
  const basic    = parseFloat(payment.basic_salary || 0);
  const advance  = parseFloat(payment.advance || 0);
  const net = Math.max(0,
    basic -
    advance -
    parseFloat(payment.deductions || 0) -
    parseFloat(payment.attendance_deduction || 0) +
    parseFloat(payment.bonus_amount || 0)
  );
  const totalCost     = basic + parseFloat(payment.allowances || 0);
  const advSettled    = Math.min(advance, basic);   // portion of advance cleared by this salary
  const advCarry      = Math.max(0, advance - basic); // remaining carry to next month
  const methodLabels = { cash: 'Cash', bank: 'Bank Transfer', jazzcash: 'JazzCash', easypaisa: 'EasyPaisa' };
  const html = `<!DOCTYPE html><html><head><title>Salary Slip — ${payment.employee_name}</title>
  <style>
    body{font-family:Arial,sans-serif;padding:40px;max-width:700px;margin:0 auto}
    .hdr{text-align:center;margin-bottom:30px;border-bottom:3px solid #1e40af;padding-bottom:20px}
    .hdr h1{color:#1e40af;margin:0;font-size:28px}.hdr p{color:#6b7280;margin:5px 0 0;font-size:14px}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:15px;margin-bottom:30px}
    .cell label{font-size:11px;text-transform:uppercase;color:#6b7280;font-weight:600;letter-spacing:.5px}
    .cell p{margin:4px 0 0;font-size:14px;color:#111827;font-weight:500}
    table{width:100%;border-collapse:collapse;margin-bottom:20px}
    th{background:#f3f4f6;text-align:left;padding:10px 16px;font-size:12px;text-transform:uppercase;color:#374151;border-bottom:2px solid #e5e7eb}
    td{padding:10px 16px;border-bottom:1px solid #e5e7eb;font-size:14px}
    .tr{text-align:right}.tot{font-weight:700;font-size:16px;background:#eff6ff}
    .tot td{border-top:2px solid #1e40af;border-bottom:2px solid #1e40af}
    .badge{display:inline-block;background:#16a34a;color:#fff;padding:8px 24px;border-radius:6px;font-weight:700;font-size:16px;margin-top:10px}
    .ftr{text-align:center;margin-top:40px;padding-top:20px;border-top:1px solid #e5e7eb;color:#9ca3af;font-size:12px}
  </style></head><body>
  <div class="hdr"><h1>OpticFlow ISP</h1><p>Salary Slip</p></div>
  <div class="grid">
    <div class="cell"><label>Employee</label><p>${payment.employee_name}</p></div>
    <div class="cell"><label>Payment Date</label><p>${payment.payment_date}</p></div>
    <div class="cell"><label>Payment Method</label><p>${methodLabels[payment.payment_method] || payment.payment_method}</p></div>
    <div class="cell"><label>Slip ID</label><p>#SP-${payment.id}</p></div>
  </div>
  <table><thead><tr><th>Description</th><th class="tr">Amount (PKR)</th></tr></thead><tbody>
    <tr><td>Basic Salary</td><td class="tr">${parseFloat(payment.basic_salary||0).toLocaleString()}</td></tr>
    <tr><td>Advance Deduction</td><td class="tr">− ${parseFloat(payment.advance||0).toLocaleString()}</td></tr>
    <tr><td>Other Deductions</td><td class="tr">− ${parseFloat(payment.deductions||0).toLocaleString()}</td></tr>
    ${parseFloat(payment.attendance_deduction||0)>0?`<tr><td>Attendance Deduction (${payment.absent_days||0} absent days)</td><td class="tr">− ${parseFloat(payment.attendance_deduction).toLocaleString()}</td></tr>`:''}
    ${parseFloat(payment.bonus_amount||0)>0?`<tr><td>Performance Bonus</td><td class="tr" style="color:#16a34a">+ ${parseFloat(payment.bonus_amount).toLocaleString()}</td></tr>`:''}
    <tr class="tot"><td>Net Payable</td><td class="tr">${net.toLocaleString()}</td></tr>
    ${parseFloat(payment.allowances||0)>0?`<tr style="color:#6b7280;font-size:12px"><td>Benefits / Lunch (non-cash)</td><td class="tr">${parseFloat(payment.allowances).toLocaleString()}</td></tr><tr style="color:#6b7280;font-size:12px"><td>Total Cost to Company</td><td class="tr">${totalCost.toLocaleString()}</td></tr>`:''}
  </tbody></table>
  ${payment.attendance_period_start?`<p style="font-size:13px;color:#374151;margin:0 0 12px"><strong>Attendance Period:</strong> ${payment.attendance_period_start} → ${payment.attendance_period_end} &nbsp;|&nbsp; Present: ${payment.present_days||0} &nbsp;|&nbsp; Absent: ${payment.absent_days||0} &nbsp;|&nbsp; Incomplete: ${payment.incomplete_days||0}</p>`:''}
  <div style="text-align:center;margin:20px 0">
    ${advance > 0 ? `
    <table style="margin:0 auto 14px;font-size:13px;border-collapse:collapse;width:auto;text-align:left">
      <tr>
        <td style="padding:4px 16px 4px 0;color:#6b7280">Advance Received This Month</td>
        <td style="padding:4px 0;font-weight:600">PKR ${advance.toLocaleString()}</td>
      </tr>
      <tr>
        <td style="padding:4px 16px 4px 0;color:#6b7280">Cash Paid on Salary Day</td>
        <td style="padding:4px 0;font-weight:600">PKR ${net.toLocaleString()}</td>
      </tr>
      ${advCarry > 0 ? `<tr>
        <td style="padding:4px 16px 4px 0;color:#dc2626">Carry Forward to Next Month</td>
        <td style="padding:4px 0;font-weight:600;color:#dc2626">PKR ${advCarry.toLocaleString()}</td>
      </tr>` : ''}
      <tr style="border-top:2px solid #e5e7eb">
        <td style="padding:8px 16px 4px 0;font-weight:700">Total Received This Month</td>
        <td style="padding:8px 0;font-weight:700">PKR ${(net + advance).toLocaleString()}</td>
      </tr>
    </table>` : `
    <p style="font-size:13px;color:#6b7280">Total Paid</p>
    <p style="font-size:28px;font-weight:700;color:#111827">PKR ${net.toLocaleString()}</p>`}
    <div class="badge">PAID</div>
  </div>
  ${payment.notes?`<p style="margin-top:20px;font-size:13px;color:#6b7280"><strong>Notes:</strong> ${payment.notes}</p>`:''}
  <div class="ftr"><p>OpticFlow ISP — Salary Payment Receipt</p></div>
  </body></html>`;
  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  w.onload = () => w.print();
};


// ── Payroll Run Banner ────────────────────────────────────────────────────────

const PayrollRunBanner = ({ run, periodStart, periodEnd, isAdmin, onUpdate }) => {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  const doAction = async (action) => {
    setLoading(action);
    setError(null);
    try {
      let res;
      if (action === 'create') {
        res = await api.post('/payroll-runs/', { period_start: periodStart, period_end: periodEnd });
      } else {
        res = await api.post(`/payroll-runs/${run.id}/${action}/`);
      }
      onUpdate(res.data);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action}.`);
    } finally {
      setLoading(null);
    }
  };

  if (!run) {
    return (
      <div className="mb-4 flex items-center justify-between px-4 py-3 bg-gray-50 border border-dashed border-gray-300 rounded-xl">
        <div>
          <p className="text-sm font-medium text-gray-600">No approval run for this period</p>
          <p className="text-xs text-gray-400 mt-0.5">Optional: create a run to enable submit → approve → lock workflow</p>
        </div>
        <button
          onClick={() => doAction('create')}
          disabled={!!loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-white rounded-lg text-xs font-medium hover:bg-gray-800 disabled:opacity-50"
        >
          {loading ? <Loader size={12} className="animate-spin" /> : <Plus size={12} />} Create Run
        </button>
      </div>
    );
  }

  const cfgs = {
    draft:    { bg: 'bg-gray-50',   border: 'border-gray-300',  text: 'text-gray-700',  label: 'Draft',            Icon: Calendar   },
    pending:  { bg: 'bg-amber-50',  border: 'border-amber-300', text: 'text-amber-800', label: 'Pending Approval', Icon: Shield     },
    approved: { bg: 'bg-green-50',  border: 'border-green-300', text: 'text-green-800', label: 'Approved',         Icon: CheckCircle},
    locked:   { bg: 'bg-blue-50',   border: 'border-blue-300',  text: 'text-blue-800',  label: 'Locked',           Icon: Lock       },
  };
  const { bg, border, text, label, Icon } = cfgs[run.status] || cfgs.draft;

  return (
    <div className={`mb-4 px-4 py-3 ${bg} border ${border} rounded-xl`}>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className={text} />
          <span className={`text-sm font-semibold ${text}`}>{label}</span>
          {run.submitted_by_name && run.status !== 'draft' && (
            <span className="text-xs text-gray-500">· Submitted by {run.submitted_by_name}</span>
          )}
          {run.approved_by_name && ['approved','locked'].includes(run.status) && (
            <span className="text-xs text-gray-500">· Approved by {run.approved_by_name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {run.status === 'draft' && (
            <button onClick={() => doAction('submit')} disabled={!!loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 disabled:opacity-50">
              {loading === 'submit' ? <Loader size={12} className="animate-spin" /> : <Send size={12} />} Submit for Approval
            </button>
          )}
          {run.status === 'pending' && isAdmin && (
            <button onClick={() => doAction('approve')} disabled={!!loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-50">
              {loading === 'approve' ? <Loader size={12} className="animate-spin" /> : <CheckCircle size={12} />} Approve
            </button>
          )}
          {run.status === 'pending' && !isAdmin && (
            <span className="text-xs text-amber-700 bg-amber-100 px-3 py-1.5 rounded-lg font-medium">Waiting for admin approval</span>
          )}
          {run.status === 'approved' && (
            <button onClick={() => doAction('lock')} disabled={!!loading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-700 text-white rounded-lg text-xs font-semibold hover:bg-blue-800 disabled:opacity-50">
              {loading === 'lock' ? <Loader size={12} className="animate-spin" /> : <Lock size={12} />} Lock Period
            </button>
          )}
          {run.status === 'locked' && (
            <span className="text-xs text-blue-700 font-semibold flex items-center gap-1"><Lock size={11} /> Period Locked</span>
          )}
        </div>
      </div>
    </div>
  );
};


// ── Attendance Import Modal ───────────────────────────────────────────────────

const AttendanceImportModal = ({ periodStart, periodEnd, onClose, onConfirmed }) => {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const salaryDate = periodEnd;
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handlePreview = async (e) => {
    e.preventDefault();
    if (!file1) { setError('Select at least the first attendance file.'); return; }
    setLoading(true); setError(null); setPreview(null);
    const fd = new FormData();
    fd.append('file1', file1);
    if (file2) fd.append('file2', file2);
    fd.append('salary_date', salaryDate);
    try {
      const res = await api.post('/attendance-imports/multi-preview/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to parse file.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview?.preview_token) return;
    setConfirming(true); setError(null);
    try {
      const res = await api.post('/attendance-imports/confirm/', { preview_token: preview.preview_token });
      setSuccess(`Imported ${res.data.created_events} punches, ${res.data.records} attendance records, ${res.data.summaries} salary summaries.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm import.');
    } finally {
      setConfirming(false);
    }
  };

  const stat = preview?.stats || {};
  const canConfirm = preview?.can_confirm && !success;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="text-blue-600" size={20} />
            <div>
              <h2 className="text-base font-bold text-gray-900">Import Attendance</h2>
              <p className="text-xs text-gray-500">Period: {periodStart} → {periodEnd}</p>
            </div>
          </div>
          <button onClick={success ? onConfirmed : onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />{error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-start gap-2">
              <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Import confirmed!</p>
                <p>{success}</p>
                <button onClick={onConfirmed} className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">
                  Done — Reload Payroll
                </button>
              </div>
            </div>
          )}

          {!success && (
            <form onSubmit={handlePreview} className="space-y-3 mb-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>File 1 — Sep 11–30 (XLS/CSV/XLSX) <span className="text-red-500">*</span></label>
                  <input type="file" accept=".csv,.xls,.xlsx"
                    onChange={(e) => setFile1(e.target.files?.[0] || null)}
                    className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>File 2 — Oct 1–10 (XLS/CSV/XLSX) <span className="text-gray-400">(optional)</span></label>
                  <input type="file" accept=".csv,.xls,.xlsx"
                    onChange={(e) => setFile2(e.target.files?.[0] || null)}
                    className={inputClass} />
                </div>
              </div>
              <p className="text-xs text-gray-400">Upload both files to cover the full payroll period (11th prev month → 10th current month). One file is accepted if it covers the whole period.</p>
              <button type="submit" disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-60">
                {loading ? <Loader size={15} className="animate-spin" /> : <Upload size={15} />} Preview Import
              </button>
            </form>
          )}

          {preview && !success && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {[['Punches', stat.events||0], ['Days', stat.records||0], ['Employees', stat.summaries||0],
                  ['Unmatched', stat.unmatched_ids||0, stat.unmatched_ids>0], ['Invalid', stat.invalid_rows||0, stat.invalid_rows>0], ['Duplicates', stat.duplicates||0]].map(([l, v, warn]) => (
                  <div key={l} className={`rounded-lg p-3 ${warn ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                    <p className="text-xs text-gray-500">{l}</p>
                    <p className={`text-xl font-bold ${warn ? 'text-yellow-700' : 'text-gray-900'}`}>{v}</p>
                  </div>
                ))}
              </div>

              {preview.unmatched_ids?.length > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm font-semibold text-yellow-800 mb-2">Unmatched device IDs — assign these in employee records before confirming</p>
                  <div className="flex flex-wrap gap-2">
                    {preview.unmatched_ids.map((id) => (
                      <span key={id} className="px-2 py-1 bg-white border border-yellow-200 rounded text-xs text-yellow-800">{id}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Present</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase">Absent</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Att. Deduction</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Payable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(preview.summaries || []).map((s) => (
                      <tr key={s.employee_id}>
                        <td className="px-3 py-2 font-medium text-gray-900">{s.employee_name}</td>
                        <td className="px-3 py-2 text-center text-green-700">{s.present_days}</td>
                        <td className="px-3 py-2 text-center text-red-600">{s.absent_days}</td>
                        <td className="px-3 py-2 text-right text-red-600">{fmt(s.attendance_deduction)}</td>
                        <td className="px-3 py-2 text-right font-bold text-blue-700">{fmt(s.payable_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <button onClick={handleConfirm} disabled={!canConfirm || confirming}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm ${canConfirm ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
                  {confirming ? <Loader size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                  Confirm Import
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ── Manual Pay Modal (for employees without attendance summary) ───────────────

const ManualPayModal = ({ employee, periodEnd, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    payment_date: periodEnd,
    payment_method: 'cash',
    amount: String(Math.max(0, parseFloat(employee.net_salary || 0))),
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const res = await api.post('/salary-payments/', {
        employee: employee.id,
        amount: parseFloat(formData.amount),
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        notes: formData.notes,
      });
      setSaved(res.data);
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === 'object'
        ? Object.entries(d).map(([k,v]) => `${k}: ${Array.isArray(v)?v.join(', '):v}`).join(' | ')
        : 'Payment failed.');
    } finally {
      setLoading(false);
    }
  };

  if (saved) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">Salary Paid</h3>
          <p className="text-sm text-gray-600 mb-1">{employee.name}</p>
          <p className="text-2xl font-bold text-green-700 mb-6">{fmt(saved.amount)}</p>
          <div className="flex gap-3">
            <button onClick={onSave} className="btn-secondary flex-1">Close</button>
            <button onClick={() => { printSalarySlip(saved); }} className="btn-primary flex-1 flex items-center justify-center gap-2">
              <Printer size={15} /> Print Slip
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-gray-900">Manual Pay — {employee.name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">No attendance data — enter amount manually</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Basic</span><span>{fmt(employee.basic_salary)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Advance</span><span className="text-red-600">− {fmt(employee.advance)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Deductions</span><span className="text-red-600">− {fmt(employee.deductions)}</span></div>
            <div className="flex justify-between font-bold border-t border-gray-200 pt-1 mt-1">
              <span>Net Payable</span><span className="text-blue-700">{fmt(employee.net_salary)}</span>
            </div>
            {parseFloat(employee.allowances) > 0 && (
              <div className="border-t border-dashed border-gray-200 pt-1 mt-1 space-y-1">
                <div className="flex justify-between text-gray-400"><span>Benefits / Lunch</span><span>{fmt(employee.allowances)}</span></div>
                <div className="flex justify-between text-gray-400"><span>Total Cost</span><span>{fmt(parseFloat(employee.basic_salary) + parseFloat(employee.allowances))}</span></div>
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>Payment Date</label>
            <input type="date" value={formData.payment_date}
              onChange={(e) => setFormData(p => ({ ...p, payment_date: e.target.value }))}
              className={inputClass} required />
          </div>
          <div>
            <label className={labelClass}>Payment Method</label>
            <select value={formData.payment_method}
              onChange={(e) => setFormData(p => ({ ...p, payment_method: e.target.value }))}
              className={inputClass}>
              <option value="cash">Cash</option>
              <option value="bank">Bank Transfer</option>
              <option value="jazzcash">JazzCash</option>
              <option value="easypaisa">EasyPaisa</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Amount (PKR)</label>
            <input type="number" value={formData.amount}
              onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))}
              className={inputClass} min="0" step="1" required />
          </div>
          <div>
            <label className={labelClass}>Notes</label>
            <textarea value={formData.notes}
              onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
              className={inputClass} rows="2" placeholder="Optional…" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-green-700 disabled:opacity-60">
              {loading && <Loader size={15} className="animate-spin" />} Pay Salary
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ── Add Advance Modal ─────────────────────────────────────────────────────────

const AddAdvanceModal = ({ employee, employees, onClose, onSave }) => {
  const [empId, setEmpId] = useState(employee?.id?.toString() || '');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!empId) { setError('Select an employee.'); return; }
    if (!amount || parseFloat(amount) <= 0) { setError('Enter a valid amount.'); return; }
    setLoading(true); setError(null);
    try {
      await api.post(`/employees/${empId}/add-advance/`, { amount: parseFloat(amount), notes });
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record advance.');
    } finally {
      setLoading(false);
    }
  };

  const selectedEmp = employee || employees?.find(e => String(e.id) === empId);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">Record Advance</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          {employee ? (
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="font-semibold text-gray-900">{employee.name}</p>
              <p className="text-sm text-gray-500">Current advance balance: <span className="font-medium text-yellow-700">{fmt(employee.advance)}</span></p>
            </div>
          ) : (
            <div>
              <label className={labelClass}>Employee</label>
              <select value={empId} onChange={(e) => setEmpId(e.target.value)} className={inputClass} required>
                <option value="">Select employee…</option>
                {employees?.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              {selectedEmp && (
                <p className="text-xs text-gray-500 mt-1">Current balance: <span className="font-medium text-yellow-700">{fmt(selectedEmp.advance)}</span></p>
              )}
            </div>
          )}

          <div>
            <label className={labelClass}>Advance Amount (PKR)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              className={inputClass} min="1" step="1" placeholder="e.g. 5000" required autoFocus />
          </div>
          <div>
            <label className={labelClass}>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              className={inputClass} rows="2" placeholder="Reason (optional)…" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-yellow-700 disabled:opacity-60">
              {loading && <Loader size={15} className="animate-spin" />} Add Advance
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ── Monthly Payroll Tab ───────────────────────────────────────────────────────

const MonthlyPayrollTab = ({ isAdmin, navYear, navMonth, periodStart, periodEnd }) => {

  const [employees, setEmployees] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [bonusPool, setBonusPool] = useState(undefined); // undefined=loading, null=none
  const [distributions, setDistributions] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [payrollRun, setPayrollRun] = useState(undefined);
  const [periodPayments, setPeriodPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showAdvances, setShowAdvances] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [manualPayEmp, setManualPayEmp] = useState(null);
  const [advanceModal, setAdvanceModal] = useState(null); // null=closed, 'general'=pick, emp=specific
  const [payingId, setPayingId] = useState(null);
  const [bonusLoading, setBonusLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [empRes, sumRes, runRes, poolRes, advRes, payRes] = await Promise.all([
        api.get('/employees/?page_size=200&is_active=true&ordering=name'),
        api.get(`/attendance-summaries/?payroll_period_start=${periodStart}&payroll_period_end=${periodEnd}&page_size=200`),
        api.get(`/payroll-runs/by-period/?period_start=${periodStart}&period_end=${periodEnd}`),
        api.get('/bonus-pools/?page_size=36'),
        api.get('/employee-advances/?page_size=1000'),
        api.get('/salary-payments/?page_size=500&ordering=-payment_date'),
      ]);

      const emps = empRes.data.results || empRes.data || [];
      const sums = sumRes.data.results || sumRes.data || [];
      setEmployees(emps);
      setSummaries(sums);
      setPayrollRun(runRes.data);

      const pools = poolRes.data.results || poolRes.data || [];
      const pool = pools.find(p => p.year === navYear && p.month === navMonth) || null;
      setBonusPool(pool);

      if (pool?.is_finalized) {
        const distRes = await api.get(`/bonus-pools/${pool.id}/distributions/`);
        setDistributions(distRes.data || []);
      } else {
        setDistributions([]);
      }

      const allAdv = advRes.data.results || advRes.data || [];
      setAdvances(allAdv.filter(a => a.date >= periodStart && a.date <= periodEnd));

      const allPay = payRes.data.results || payRes.data || [];
      const pad = (n) => String(n).padStart(2, '0');
      const monthStr = `${navYear}-${pad(navMonth)}`;
      setPeriodPayments(allPay.filter(p => p.payment_date?.startsWith(monthStr)));

    } catch (err) {
      setError('Failed to load payroll data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [periodStart, periodEnd, navYear, navMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  // Merge employees with their summaries, distributions, and payments
  const rows = employees.map(emp => {
    const summary = summaries.find(s => s.employee === emp.id) || null;
    const dist = distributions.find(d => d.employee === emp.id) || null;
    const empPays = periodPayments.filter(p => p.employee === emp.id);
    const isPaid = summary ? summary.is_paid : empPays.length > 0;
    const paidPayment = isPaid ? (empPays[0] || null) : null;

    const basic = parseFloat(emp.basic_salary || 0);
    const allowances = parseFloat(emp.allowances || 0);
    const advance = parseFloat(emp.advance || 0);
    const deductions = parseFloat(emp.deductions || 0);
    const attDed = summary ? parseFloat(summary.attendance_deduction || 0) : null;
    const bonus = dist ? parseFloat(dist.amount || 0) : 0;
    const payable = summary
      ? parseFloat(summary.payable_amount || 0) + bonus
      : Math.max(0, basic - advance - deductions + bonus);

    return { emp, summary, dist, isPaid, paidPayment, basic, allowances, advance, deductions, attDed, bonus, payable };
  });

  const totalPayable = rows.reduce((s, r) => s + r.payable, 0);
  const paidCount = rows.filter(r => r.isPaid).length;
  const unpaidCount = rows.filter(r => !r.isPaid).length;
  const isLocked = payrollRun?.status === 'locked';

  const handleCalculateBonus = async () => {
    setBonusLoading(true); setError(null);
    try {
      const res = await api.post('/bonus-pools/calculate/', { year: navYear, month: navMonth });
      setBonusPool(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to calculate bonus.');
    } finally {
      setBonusLoading(false);
    }
  };

  const handleFinalizeBonus = async () => {
    if (!bonusPool) return;
    if (!window.confirm(
      `Finalize bonus pool of ${fmt(bonusPool.total_bonus_amount)} for ${MONTH_NAMES[navMonth-1]} ${navYear}?\n\n` +
      `${fmt(bonusPool.per_employee_bonus)} per employee (${bonusPool.eligible_employees_count} eligible).\n\nThis cannot be undone.`
    )) return;
    setBonusLoading(true); setError(null);
    try {
      const res = await api.post(`/bonus-pools/${bonusPool.id}/finalize/`);
      setBonusPool(res.data);
      const distRes = await api.get(`/bonus-pools/${res.data.id}/distributions/`);
      setDistributions(distRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to finalize bonus.');
    } finally {
      setBonusLoading(false);
    }
  };

  const paySingle = async (row) => {
    setPayingId(row.emp.id); setError(null);
    try {
      await api.post(`/attendance-summaries/${row.summary.id}/create-payment/`, {
        payment_date: periodEnd,
        payment_method: 'cash',
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed.');
    } finally {
      setPayingId(null);
    }
  };

  const dash = <span className="text-gray-300">—</span>;

  return (
    <div>
      {/* Summary Cards */}
      <div className="flex justify-end mb-3">
        <button onClick={loadData} disabled={loading}
          className="btn-secondary flex items-center gap-2 text-sm">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="card p-3">
          <p className="text-xs text-gray-500 uppercase font-medium">Total Payroll</p>
          <p className="text-sm font-bold text-blue-700 mt-1">{fmt(totalPayable)}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500 uppercase font-medium">Employees</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{rows.length}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500 uppercase font-medium">Paid</p>
          <p className="text-xl font-bold text-green-700 mt-1">{paidCount}</p>
        </div>
        <div className="card p-3">
          <p className={`text-xs uppercase font-medium ${unpaidCount > 0 ? 'text-amber-600' : 'text-gray-500'}`}>Unpaid</p>
          <p className={`text-xl font-bold mt-1 ${unpaidCount > 0 ? 'text-amber-700' : 'text-gray-400'}`}>{unpaidCount}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-5">
        <button onClick={() => setShowImport(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
          <Upload size={15} /> Import Attendance
        </button>
        <button onClick={async () => {
          try {
            const res = await api.get('/attendance-report/export/', {
              params: { period_start: period.start, period_end: period.end },
              responseType: 'blob',
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Attendance_Report_${period.start}_${period.end}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
          } catch {
            alert('Failed to generate report.');
          }
        }} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium">
          <Download size={15} /> Export Report
        </button>

        {bonusPool === undefined ? null : !bonusPool ? (
          <button onClick={handleCalculateBonus} disabled={bonusLoading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium disabled:opacity-60">
            {bonusLoading ? <Loader size={15} className="animate-spin" /> : <Star size={15} />}
            Calculate Bonus
          </button>
        ) : bonusPool.is_finalized ? (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 font-medium">
            <CheckCircle size={15} />
            Bonus Finalized — {fmt(bonusPool.per_employee_bonus)}/employee
            ({bonusPool.new_installs_count} new + {bonusPool.shifted_count} shifts = {fmt(bonusPool.total_bonus_amount)})
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700">
              <Star size={14} />
              Pool: {fmt(bonusPool.total_bonus_amount)} — {fmt(bonusPool.per_employee_bonus)}/employee
            </div>
            <button onClick={handleFinalizeBonus} disabled={bonusLoading}
              className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium disabled:opacity-60">
              {bonusLoading ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />} Finalize Bonus
            </button>
            <button onClick={handleCalculateBonus} disabled={bonusLoading}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" title="Recalculate">
              <RefreshCw size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Payroll Run Banner */}
      {payrollRun !== undefined && (
        <PayrollRunBanner
          run={payrollRun}
          periodStart={periodStart}
          periodEnd={periodEnd}
          isAdmin={isAdmin}
          onUpdate={r => setPayrollRun(r)}
        />
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {/* Main Employee Table */}
      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader className="animate-spin text-blue-600" size={28} />
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-12 text-center text-gray-400">
          <Users size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="font-medium text-gray-600">No active employees</p>
        </div>
      ) : (
        <div className="card overflow-hidden mb-4">
          {isLocked && (
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2 text-xs text-blue-700">
              <Lock size={13} /> Period locked — no further payments allowed.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Basic</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Benefits</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Advance</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Att. Ded.</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Bonus</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Payable</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(row => (
                  <tr key={row.emp.id} className={`hover:bg-gray-50 transition-colors ${row.isPaid ? 'opacity-70' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{row.emp.name}</div>
                      <div className="text-xs text-gray-400">{row.emp.designation}</div>
                      {!row.summary && !row.isPaid && (
                        <div className="text-xs text-amber-500 mt-0.5">No attendance data</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">{fmt(row.basic)}</td>
                    <td className="px-4 py-3 text-right text-gray-400 text-xs italic">
                      {row.allowances > 0 ? fmt(row.allowances) : dash}
                    </td>
                    <td className="px-4 py-3 text-right text-red-600">
                      {row.advance > 0 ? `−${fmt(row.advance)}` : dash}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.attDed === null
                        ? <span className="text-xs text-gray-300 italic">—</span>
                        : row.attDed > 0
                          ? <span className="text-red-600">−{fmt(row.attDed)}</span>
                          : <span className="text-gray-400 text-xs">0</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-purple-600">
                      {row.bonus > 0 ? `+${fmt(row.bonus)}` : dash}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-blue-700">{fmt(row.payable)}</td>
                    <td className="px-4 py-3 text-center">
                      {row.isPaid
                        ? <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold"><CheckCircle size={11} /> PAID</span>
                        : <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">UNPAID</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {row.isPaid && row.paidPayment && (
                          <button onClick={() => printSalarySlip(row.paidPayment)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Print Slip">
                            <Printer size={15} />
                          </button>
                        )}
                        {!row.isPaid && !isLocked && (
                          row.summary ? (
                            <button onClick={() => paySingle(row)} disabled={payingId === row.emp.id}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-xs font-medium">
                              {payingId === row.emp.id && <Loader size={11} className="animate-spin" />}
                              Pay
                            </button>
                          ) : (
                            <button onClick={() => setManualPayEmp(row.emp)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-xs font-medium">
                              <CreditCard size={12} /> Manual
                            </button>
                          )
                        )}
                        {!row.isPaid && (
                          <button onClick={() => setAdvanceModal(row.emp)}
                            className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                            title="Add Advance">
                            <Banknote size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-700 text-sm">Totals</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-700 text-sm">{fmt(rows.reduce((s,r)=>s+r.basic,0))}</td>
                  <td className="px-4 py-3 text-right font-bold text-green-700 text-sm">{fmt(rows.reduce((s,r)=>s+r.allowances,0))}</td>
                  <td className="px-4 py-3 text-right font-bold text-red-700 text-sm">{fmt(rows.reduce((s,r)=>s+r.advance,0))}</td>
                  <td className="px-4 py-3 text-right font-bold text-red-700 text-sm">{fmt(rows.reduce((s,r)=>s+(r.attDed||0),0))}</td>
                  <td className="px-4 py-3 text-right font-bold text-purple-700 text-sm">{fmt(rows.reduce((s,r)=>s+r.bonus,0))}</td>
                  <td className="px-4 py-3 text-right font-bold text-blue-700 text-sm">{fmt(totalPayable)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Advances Section */}
      <div className="card overflow-hidden">
        <button onClick={() => setShowAdvances(!showAdvances)}
          className="w-full px-4 py-3 flex items-center justify-between text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <Banknote size={16} className="text-yellow-600" />
            Advances this period
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold">{advances.length}</span>
            {advances.length > 0 && (
              <span className="text-yellow-700">{fmt(advances.reduce((s,a)=>s+parseFloat(a.amount||0),0))}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={(e) => { e.stopPropagation(); setAdvanceModal('general'); }}
              className="flex items-center gap-1 px-2.5 py-1 bg-yellow-600 text-white rounded-lg text-xs font-medium hover:bg-yellow-700">
              <Plus size={12} /> Add
            </button>
            {showAdvances ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </button>
        {showAdvances && (
          <div className="border-t border-gray-100">
            {advances.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-gray-400">No advances recorded for this period.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-yellow-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {advances.map(a => (
                      <tr key={a.id} className="hover:bg-yellow-50/40">
                        <td className="px-4 py-2 font-medium text-gray-900">{a.employee_name || a.employee}</td>
                        <td className="px-4 py-2 text-gray-600">{a.date}</td>
                        <td className="px-4 py-2 text-right font-bold text-yellow-700">{fmt(a.amount)}</td>
                        <td className="px-4 py-2 text-gray-400 text-xs">{a.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showImport && (
        <AttendanceImportModal
          periodStart={periodStart}
          periodEnd={periodEnd}
          onClose={() => setShowImport(false)}
          onConfirmed={() => { setShowImport(false); loadData(); }}
        />
      )}
      {manualPayEmp && (
        <ManualPayModal
          employee={manualPayEmp}
          periodEnd={periodEnd}
          onClose={() => setManualPayEmp(null)}
          onSave={() => { setManualPayEmp(null); loadData(); }}
        />
      )}
      {advanceModal !== null && (
        <AddAdvanceModal
          employee={advanceModal === 'general' ? null : advanceModal}
          employees={employees}
          onClose={() => setAdvanceModal(null)}
          onSave={() => { setAdvanceModal(null); loadData(); }}
        />
      )}
    </div>
  );
};


// ── Salary History Tab ────────────────────────────────────────────────────────

const SalaryHistoryTab = ({ employees, navYear, navMonth }) => {
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [methodFilter, setMethodFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page_size: 500, ordering: '-payment_date' });
      if (selectedEmp) params.set('employee', selectedEmp);
      if (methodFilter) params.set('payment_method', methodFilter);
      const res = await api.get(`/salary-payments/?${params}`);
      setAllPayments(res.data.results || res.data || []);
    } catch {
      setError('Failed to load salary payments.');
    } finally {
      setLoading(false);
    }
  }, [selectedEmp, methodFilter]);

  useEffect(() => { load(); }, [load]);

  // Filter by the selected month from the global navigator
  const pad = (n) => String(n).padStart(2, '0');
  const monthPrefix = `${navYear}-${pad(navMonth)}`;
  const payments = allPayments.filter(p => p.payment_date?.startsWith(monthPrefix));

  return (
    <div>
      <div className="card p-4 mb-5 flex flex-wrap items-end gap-3">
        <div>
          <label className={labelClass}>Employee</label>
          <select value={selectedEmp} onChange={(e) => setSelectedEmp(e.target.value)} className={inputClass} style={{ width: 220 }}>
            <option value="">All Employees</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Method</label>
          <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)} className={inputClass} style={{ width: 150 }}>
            <option value="">All</option>
            <option value="cash">Cash</option>
            <option value="bank">Bank Transfer</option>
            <option value="jazzcash">JazzCash</option>
            <option value="easypaisa">EasyPaisa</option>
          </select>
        </div>
        {(selectedEmp || methodFilter) && (
          <button onClick={() => { setSelectedEmp(''); setMethodFilter(''); }}
            className="btn-secondary flex items-center gap-1 text-sm"><X size={13} /> Clear</button>
        )}
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}

      <div className="mb-3 text-sm text-gray-500">
        {payments.length} payment{payments.length !== 1 ? 's' : ''} in {MONTH_NAMES[navMonth - 1]} {navYear}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader className="animate-spin text-blue-600" size={28} /></div>
      ) : payments.length === 0 ? (
        <div className="card p-12 text-center text-gray-400">
          <Banknote size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="font-medium text-gray-600">No salary payments found</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Basic</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Att. Ded.</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Bonus</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Paid</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Method</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Slip</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{p.employee_name}</div>
                      {p.attendance_period_start && (
                        <div className="text-xs text-gray-400">{p.attendance_period_start} → {p.attendance_period_end}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{p.payment_date}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{fmt(p.basic_salary)}</td>
                    <td className="px-4 py-3 text-right text-red-600">{parseFloat(p.attendance_deduction||0)>0?`−${fmt(p.attendance_deduction)}`:'—'}</td>
                    <td className="px-4 py-3 text-right text-purple-600">{parseFloat(p.bonus_amount||0)>0?`+${fmt(p.bonus_amount)}`:'—'}</td>
                    <td className="px-4 py-3 text-right font-bold text-blue-700">{fmt(p.amount)}</td>
                    <td className="px-4 py-3 text-center"><span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs capitalize">{p.payment_method}</span></td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => printSalarySlip(p)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                        <Printer size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


// ── YTD Summary Tab ───────────────────────────────────────────────────────────

const YTDTab = ({ navYear }) => {
  const [year, setYear] = useState(navYear || new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setYear(navYear); }, [navYear]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(`/employees/ytd/?year=${year}`);
      setData(res.data);
    } catch {
      setError('Failed to load YTD summary.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const exportCSV = () => {
    if (!data?.employees?.length) return;
    const headers = ['Employee','Designation','Department','Months Paid','Total Paid','Basic','Allowances','Advance','Deductions','Att. Ded.','Bonus'];
    const rows = data.employees.map(e => [
      e.employee_name,e.designation,e.department,e.months_paid,
      e.ytd_amount,e.ytd_basic,e.ytd_allowances,e.ytd_advance,
      e.ytd_deductions,e.ytd_attendance_deduction,e.ytd_bonus,
    ]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${v??''}"`).join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href=url; a.download=`ytd-${data.year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const grand = data?.employees?.reduce((s,e)=>s+parseFloat(e.ytd_amount||0),0)||0;

  return (
    <div>
      <div className="card p-4 mb-5 flex flex-wrap items-end gap-3">
        <div>
          <label className={labelClass}>Year</label>
          <input type="number" value={year} onChange={e=>setYear(Number(e.target.value))}
            className={inputClass} style={{width:110}} min="2020" max="2100" />
        </div>
        <button onClick={load} disabled={loading} className="btn-primary flex items-center gap-2">
          {loading?<Loader size={15} className="animate-spin"/>:<TrendingUp size={15}/>} Load YTD
        </button>
        {data?.employees?.length>0 && (
          <button onClick={exportCSV} className="btn-secondary flex items-center gap-2">
            <Download size={15}/> Export CSV
          </button>
        )}
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2"><AlertCircle size={16}/>{error}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[['Year',data.year,'text-gray-900'],['Employees',data.employees.length,'text-gray-900'],
              ['Paid',data.employees.filter(e=>e.months_paid>0).length,'text-green-700'],
              ['Total YTD',fmt(grand),'text-blue-700 text-sm']].map(([l,v,c])=>(
              <div key={l} className="card p-3">
                <p className="text-xs text-gray-500 uppercase font-medium">{l}</p>
                <p className={`font-bold mt-1 ${c}`}>{v}</p>
              </div>
            ))}
          </div>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Months</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total Paid</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Basic</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Allowances</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Att. Ded.</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Advance</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Bonus</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.employees.map(e=>(
                    <tr key={e.employee_id} className={`hover:bg-gray-50 ${e.months_paid===0?'opacity-50':''}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{e.employee_name}</div>
                        <div className="text-xs text-gray-400">{e.designation}{e.department?` · ${e.department}`:''}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${e.months_paid>0?'bg-green-100 text-green-700':'bg-gray-100 text-gray-500'}`}>{e.months_paid}/12</span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-blue-700">{fmt(e.ytd_amount)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{fmt(e.ytd_basic)}</td>
                      <td className="px-4 py-3 text-right text-green-600">{parseFloat(e.ytd_allowances)>0?fmt(e.ytd_allowances):'—'}</td>
                      <td className="px-4 py-3 text-right text-red-600">{parseFloat(e.ytd_attendance_deduction)>0?`−${fmt(e.ytd_attendance_deduction)}`:'—'}</td>
                      <td className="px-4 py-3 text-right text-amber-700">{parseFloat(e.ytd_advance)>0?`−${fmt(e.ytd_advance)}`:'—'}</td>
                      <td className="px-4 py-3 text-right text-purple-600">{parseFloat(e.ytd_bonus)>0?`+${fmt(e.ytd_bonus)}`:'—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-4 py-3 font-bold text-gray-700">Total</td>
                    <td/>
                    <td className="px-4 py-3 text-right font-bold text-blue-700">{fmt(grand)}</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-700">{fmt(data.employees.reduce((s,e)=>s+parseFloat(e.ytd_basic||0),0))}</td>
                    <td className="px-4 py-3 text-right font-bold text-green-600">{fmt(data.employees.reduce((s,e)=>s+parseFloat(e.ytd_allowances||0),0))}</td>
                    <td className="px-4 py-3 text-right font-bold text-red-600">{fmt(data.employees.reduce((s,e)=>s+parseFloat(e.ytd_attendance_deduction||0),0))}</td>
                    <td className="px-4 py-3 text-right font-bold text-amber-700">{fmt(data.employees.reduce((s,e)=>s+parseFloat(e.ytd_advance||0),0))}</td>
                    <td className="px-4 py-3 text-right font-bold text-purple-600">{fmt(data.employees.reduce((s,e)=>s+parseFloat(e.ytd_bonus||0),0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};


// ── Main Payroll Page ─────────────────────────────────────────────────────────

const TABS = [
  { id: 'monthly', label: 'Monthly Payroll', icon: Calendar },
  { id: 'history', label: 'Salary History',  icon: Banknote  },
  { id: 'ytd',     label: 'YTD Summary',     icon: TrendingUp },
];

const Payroll = () => {
  const today = new Date();
  const [activeTab, setActiveTab] = useState('monthly');
  const [employees, setEmployees] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  // Global month navigator — shared across all tabs
  const [navYear, setNavYear] = useState(today.getFullYear());
  const [navMonth, setNavMonth] = useState(today.getMonth() + 1);
  const { start: periodStart, end: periodEnd } = getPeriod(navYear, navMonth);

  const navigate = (dir) => {
    let m = navMonth + dir, y = navYear;
    if (m > 12) { m = 1; y++; }
    if (m < 1)  { m = 12; y--; }
    setNavMonth(m); setNavYear(y);
  };

  useEffect(() => {
    api.get('/employees/?page_size=200&is_active=true&ordering=name')
      .then(res => setEmployees(res.data.results || res.data || []))
      .catch(() => {});
    api.get('/users/me/')
      .then(res => setIsAdmin(res.data?.is_superuser || false))
      .catch(() => {});
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
      {/* Page header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Banknote className="text-blue-600" size={26} /> Payroll
        </h1>
      </div>

      {/* Global Month Navigator */}
      <div className="flex items-center justify-between mb-5 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-600">
            <ChevronLeft size={20} />
          </button>
          <div className="text-center min-w-[200px]">
            <p className="text-lg font-bold text-gray-900">{MONTH_NAMES[navMonth - 1]} {navYear}</p>
            <p className="text-xs text-gray-400">Period: {periodStart} → {periodEnd}</p>
          </div>
          <button onClick={() => navigate(1)}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-600">
            <ChevronRight size={20} />
          </button>
        </div>
        <button
          onClick={() => { setNavYear(today.getFullYear()); setNavMonth(today.getMonth() + 1); }}
          className="text-xs text-blue-600 hover:underline font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
        >
          Today
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white border border-gray-200 rounded-xl p-1 w-fit shadow-sm overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap
                ${active ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
              <Icon size={15} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {activeTab === 'monthly' && (
        <MonthlyPayrollTab
          isAdmin={isAdmin}
          navYear={navYear}
          navMonth={navMonth}
          periodStart={periodStart}
          periodEnd={periodEnd}
        />
      )}
      {activeTab === 'history' && (
        <SalaryHistoryTab
          employees={employees}
          navYear={navYear}
          navMonth={navMonth}
        />
      )}
      {activeTab === 'ytd' && <YTDTab navYear={navYear} />}
    </div>
  );
};

export default Payroll;
